import { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  UserCredential,
} from "firebase/auth";
import { auth } from "../data/firebase";
import analytics from "../lib/analytics";
import { AccessState, NO_ACCESS, deriveAccessFromClaims } from "../data/access";

interface AuthContextType {
  currentUser: User | null;
  /** Derived access state; the only thing UI should gate on. */
  access: AccessState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  claims: any;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  refreshCustomClaims: () => Promise<void>;
  loading: boolean;
  canAccessApp: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function useAuthProtected() {
  const { currentUser, ...rest } = useAuth();
  return { currentUser: currentUser as User, ...rest };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [access, setAccess] = useState<AccessState>(NO_ACCESS);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [claims, setClaims] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const applyToken = async (user: User) => {
    // Force refresh so freshly written claims are picked up.
    await user.getIdToken(true);
    const tokenResult = await user.getIdTokenResult();
    setClaims(tokenResult.claims);
    setAccess(deriveAccessFromClaims(tokenResult.claims));
  };

  const refreshCustomClaims = async () => {
    if (currentUser) {
      await applyToken(currentUser);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        analytics.identify(user.uid);
        // Resolve claims before exposing the user, so no render sees a
        // signed-in user with NO_ACCESS and redirects to the payment page.
        await applyToken(user);
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setAccess(NO_ACCESS);
        setClaims(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function signup(email: string, password: string) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result;
  }

  async function sendVerificationEmail() {
    if (currentUser && !currentUser.emailVerified) {
      await sendEmailVerification(currentUser);
    }
  }

  async function login(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  async function changePassword(newPassword: string) {
    if (currentUser) {
      await updatePassword(currentUser, newPassword);
    }
  }

  // Paying users can use the app even if their email is not verified.
  function canAccessApp() {
    if (!currentUser) return false;
    if (access.hasAccess) return true;
    return currentUser.emailVerified;
  }

  const value = {
    currentUser,
    access,
    claims,
    login,
    signup,
    logout,
    sendVerificationEmail,
    resetPassword,
    changePassword,
    refreshCustomClaims,
    loading,
    canAccessApp,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
